import { FaTimes, FaImage, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { resolverImagenUrl } from "../../../utils/imagen";

export default function CategoriaModal({
  open,
  modoEdicion,
  categoria,
  onChange,
  onClose,
  onSave,
}) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);

  /* ===============================
     PREVIEW (solo cambia al cambiar categoría)
  ================================ */
  useEffect(() => {
    if (categoria?.imagen && typeof categoria.imagen === "string") {
      setPreview(categoria.imagen);
    } else {
      setPreview(null);
    }
  }, [categoria?.id]);

  /* ===============================
     CAMBIO DE IMAGEN
  ================================ */
  const onImagenChange = (file) => {
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen no puede superar los 3MB");
      return;
    }

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato de imagen no permitido");
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    onChange("imagenFile", file);
  };

  const quitarImagen = () => {
    setPreview(null);
    onChange("imagen", null);
    onChange("imagenFile", null);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="
          bg-white/95 dark:bg-slate-900/90
          text-slate-900 dark:text-slate-100
          rounded-2xl shadow-2xl
          border border-slate-200 dark:border-slate-800
          w-full max-w-md mx-4
        "
      >
        {/* ===============================
            HEADER
        =============================== */}
        <div
          className="
            flex items-center justify-between px-5 py-3
            border-b border-slate-200 dark:border-slate-800
            bg-slate-100 dark:bg-slate-950/80
            rounded-t-2xl
          "
        >
          <h3 className="text-sm font-semibold text-amber-500 dark:text-amber-300">
            {modoEdicion ? "✏️ Editar Categoría" : "➕ Nueva Categoría"}
          </h3>

          <button
            onClick={onClose}
            className="
              p-1.5 rounded-full border
              border-slate-300 dark:border-slate-700
              text-slate-500 dark:text-slate-300
              hover:bg-slate-200 dark:hover:bg-slate-800
              transition
            "
          >
            <FaTimes />
          </button>
        </div>

        {/* ===============================
            BODY
        =============================== */}
        <div className="px-5 py-4 space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-300">
              Nombre
            </label>
            <input
              value={categoria.nombre}
              onChange={(e) => onChange("nombre", e.target.value)}
              className="
                w-full rounded-lg border
                border-slate-300 dark:border-slate-600
                bg-white dark:bg-slate-900/70
                px-3 py-2 text-sm
                text-slate-900 dark:text-slate-100
                placeholder:text-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-400
              "
            />
          </div>

          {/* Orden */}
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-300">
              Orden
            </label>
            <input
              type="number"
              value={categoria.orden}
              onChange={(e) =>
                onChange("orden", Number(e.target.value))
              }
              className="
                w-full rounded-lg border
                border-slate-300 dark:border-slate-600
                bg-white dark:bg-slate-900/70
                px-3 py-2 text-sm
                text-slate-900 dark:text-slate-100
                focus:outline-none focus:ring-2 focus:ring-emerald-400
              "
            />
          </div>

          {/* IMAGEN */}
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-300">
              Imagen de la categoría (opcional)
            </label>

            {preview ? (
              <div className="relative">
                <img
                  src={resolverImagenUrl(preview)}
                  alt="preview"
                  className="mt-1 w-full h-36 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={quitarImagen}
                  className="absolute top-2 right-2 bg-red-600 text-white
                             p-2 rounded-full shadow hover:bg-red-700"
                >
                  <FaTrash />
                </button>
              </div>
            ) : (
              <label
                className="
                  flex items-center justify-center gap-2
                  w-full h-32 rounded-lg border-2 border-dashed
                  text-slate-400 cursor-pointer
                  hover:bg-slate-50 dark:hover:bg-slate-800
                "
              >
                <FaImage />
                <span className="text-sm">Agregar imagen</span>
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => onImagenChange(e.target.files[0])}
                />
              </label>
            )}
          </div>

          {/* Activo */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={categoria.activo}
              onChange={(e) =>
                onChange("activo", e.target.checked)
              }
              className="
                h-4 w-4 rounded
                border-slate-300 dark:border-slate-600
                text-emerald-500 focus:ring-emerald-400
              "
            />
            <span className="text-sm text-slate-700 dark:text-slate-200">
              Categoría activa
            </span>
          </div>
        </div>

        {/* ===============================
            FOOTER
        =============================== */}
        <div
          className="
            flex justify-end gap-2 px-5 py-3
            border-t border-slate-200 dark:border-slate-800
            bg-slate-50 dark:bg-slate-950/60
            rounded-b-2xl
          "
        >
          <button
            onClick={onClose}
            className="
              px-4 py-2 rounded-lg text-sm font-medium
              border border-slate-300 dark:border-slate-600
              text-slate-700 dark:text-slate-200
              hover:bg-slate-100 dark:hover:bg-slate-800
              transition
            "
          >
            Cancelar
          </button>

          <button
            onClick={async () => {
              await onSave();
              navigate("/admin/categorias");
            }}
            className="
              px-4 py-2 rounded-lg text-sm font-semibold
              bg-emerald-500 hover:bg-emerald-600
              text-white transition
            "
          >
            {modoEdicion ? "Guardar cambios" : "Crear categoría"}
          </button>
        </div>
      </div>
    </div>
  );
}
